import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Building2, FileEdit, Search, ShieldCheck, Wallet } from "lucide-react";
import { EmptyState, PageHeader, Spinner, StatusBadge } from "../../components/common";
import { CorrectedBadge, DemandEditDialog } from "../../components/DemandEditDialog";
import { CollectionSummaryCards } from "../../components/PropertyCollectionSummary";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { api, errMsg, fmtINR } from "../../lib/api";

/**
 * Phase 188 — Super Admin › Demand Corrections.
 * Pick a municipality, look a property up by PIN, correct any of its demands (Arrear / Current / Penalty / Rebate) with a
 * mandatory reason. Same rules as the Municipal Admin's property sheet: excess money becomes credit, a larger demand raises
 * the balance, every correction is audited in that municipality's trail.
 */
export default function DemandCorrections() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [munis, setMunis] = useState(null);
  const code = searchParams.get("muni") || "";
  const [pin, setPin] = useState(searchParams.get("pin") || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editDemand, setEditDemand] = useState(null);

  useEffect(() => {
    api.get("/platform/municipalities").then((r) => setMunis((r.data || []).filter((m) => !m.is_deleted))).catch((e) => toast.error(errMsg(e)));
  }, []);

  const setParam = (k, v) => {
    const next = new URLSearchParams(searchParams);
    if (v) next.set(k, v); else next.delete(k);
    setSearchParams(next, { replace: true });
  };

  const lookup = useCallback(async (c = code, p = pin) => {
    if (!c) return toast.error("Choose a municipality first");
    if (!p.trim()) return toast.error("Enter a property PIN");
    setLoading(true); setError(null);
    try {
      const r = await api.get(`/platform/municipalities/${c}/properties/pin-lookup`, { params: { pin: p.trim() } });
      setData(r.data);
      setParam("pin", p.trim());
    } catch (e) {
      setData(null); setError(errMsg(e));
    } finally { setLoading(false); }
  }, [code, pin]); // eslint-disable-line react-hooks/exhaustive-deps

  // deep link: ?muni=bmc&pin=BMC100001 loads straight away
  useEffect(() => {
    if (code && searchParams.get("pin") && !data && !loading && munis) lookup(code, searchParams.get("pin"));
  }, [munis]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSaved = () => lookup();
  const prop = data?.property;

  return (
    <div className="space-y-5">
      <PageHeader title="Demand Corrections" subtitle="Correct a property's demand — even when it is partly or fully paid — with a mandatory reason. Excess payments become credit against the property; every correction is written to the municipality's audit trail.">
        <ShieldCheck className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
      </PageHeader>

      <Card className="shadow-sm">
        <CardContent className="p-4">
          <form className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] gap-3 items-end" onSubmit={(e) => { e.preventDefault(); lookup(); }} data-testid="demand-corrections-form">
            <div className="space-y-1.5">
              <Label className="text-xs">Municipality</Label>
              <Select value={code} onValueChange={(v) => { setParam("muni", v); setData(null); setError(null); }}>
                <SelectTrigger data-testid="demand-corrections-muni-select"><SelectValue placeholder={munis ? "Choose a municipality" : "Loading…"} /></SelectTrigger>
                <SelectContent>
                  {(munis || []).map((m) => (
                    <SelectItem key={m.code} value={m.code} data-testid={`demand-corrections-muni-${m.code}`}>
                      <span className="inline-flex items-center gap-2"><Building2 className="h-3.5 w-3.5 text-muted-foreground" /> {m.name} <span className="text-muted-foreground font-mono text-xs">{m.code}</span></span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="demand-corrections-pin" className="text-xs">Property PIN</Label>
              <Input id="demand-corrections-pin" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="e.g. BMC100001" className="font-mono" data-testid="demand-corrections-pin-input" />
            </div>
            <Button type="submit" disabled={loading || !code || !pin.trim()} data-testid="demand-corrections-lookup-button">
              <Search className="h-4 w-4 mr-2" /> {loading ? "Looking up…" : "Look up"}
            </Button>
          </form>
          {error && <p className="mt-3 text-sm text-destructive" data-testid="demand-corrections-error">{error}</p>}
        </CardContent>
      </Card>

      {loading && !data && <Spinner />}

      {!loading && !data && !error && (
        <EmptyState title="Look up a property" subtitle="Choose the municipality and enter the property's PIN to see its demands." />
      )}

      {data && prop && (
        <Card className="shadow-sm" data-testid="demand-corrections-property">
          <CardContent className="p-4 space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-mono text-lg font-semibold" data-testid="demand-corrections-property-pin">{prop.pin}</p>
                <p className="text-sm">{prop.owner_name}{prop.father_spouse_name ? <span className="text-muted-foreground"> · {prop.father_spouse_name}</span> : null}</p>
                <p className="text-xs text-muted-foreground">{[prop.address, prop.mohalla_label, prop.ward_label, prop.zone_label].filter(Boolean).join(" · ")} · {data.municipality?.name}</p>
              </div>
              <StatusBadge status={prop.status} />
            </div>

            <CollectionSummaryCards summary={data.collection_summary} testId="demand-corrections-summary" />
            {Number(prop.advance_credit) > 0.004 && (
              <p className="text-xs text-primary flex items-center gap-1.5" data-testid="demand-corrections-credit">
                <Wallet className="h-3.5 w-3.5" /> {fmtINR(prop.advance_credit)} held as credit against this property.
              </p>
            )}

            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="font-medium text-sm">Demands</p>
                <p className="text-[11px] text-muted-foreground">Use the pencil to correct a demand — a reason is required.</p>
              </div>
              <div className="border rounded-lg overflow-hidden">
                <Table data-testid="demand-corrections-demands">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Head</TableHead>
                      <TableHead className="text-right">Arrear</TableHead>
                      <TableHead className="text-right">Current</TableHead>
                      <TableHead className="text-right">Penalty / Rebate</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-right">Paid</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-24"><span className="sr-only">Edit</span></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.demands.length === 0 && (
                      <TableRow><TableCell colSpan={9} className="text-center text-sm text-muted-foreground py-6">This property has no demands.</TableCell></TableRow>
                    )}
                    {data.demands.map((d) => (
                      <TableRow key={d.id} data-testid={`demand-corrections-row-${d.id}`}>
                        <TableCell className="text-sm">
                          <span className="flex items-center gap-1.5 flex-wrap">{d.tax_head_name}<CorrectedBadge demand={d} testId={`demand-corrections-corrected-${d.id}`} /></span>
                          <p className="text-[11px] text-muted-foreground">{d.fy_label}</p>
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-sm">{fmtINR(d.arrear_amount)}</TableCell>
                        <TableCell className="text-right tabular-nums text-sm">{fmtINR(d.current_amount)}</TableCell>
                        <TableCell className="text-right tabular-nums text-sm text-muted-foreground">{Number(d.penalty_amount) > 0 ? `+${fmtINR(d.penalty_amount)}` : ""}{Number(d.penalty_amount) > 0 && Number(d.rebate_amount) > 0 ? " / " : ""}{Number(d.rebate_amount) > 0 ? `−${fmtINR(d.rebate_amount)}` : ""}{!(Number(d.penalty_amount) > 0) && !(Number(d.rebate_amount) > 0) ? "—" : ""}</TableCell>
                        <TableCell className="text-right tabular-nums text-sm" data-testid={`demand-corrections-total-${d.id}`}>{fmtINR(d.total_demand)}</TableCell>
                        <TableCell className="text-right tabular-nums text-sm" data-testid={`demand-corrections-paid-${d.id}`}>{fmtINR(d.paid_amount)}</TableCell>
                        <TableCell className="text-right tabular-nums text-sm font-medium" data-testid={`demand-corrections-balance-${d.id}`}>{fmtINR(d.balance_amount)}</TableCell>
                        <TableCell><StatusBadge status={d.payment_status} /></TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditDemand(d)} data-testid={`demand-corrections-edit-${d.id}`}>
                            <FileEdit className="h-3.5 w-3.5 mr-1" /> Correct
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <DemandEditDialog code={code} demand={editDemand} pin={prop?.pin} open={!!editDemand} mode="platform"
        onOpenChange={(v) => { if (!v) setEditDemand(null); }} onSaved={onSaved} />
    </div>
  );
}
